import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Container,
    Paper,
    Dialog,
    DialogContent,
    DialogActions,
    InputAdornment,
    IconButton,
    Alert
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '@df/api';

const ChangePassword = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        new_password_confirm: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalStatus, setModalStatus] = useState(null); // 'success' | 'error'
    const [modalMessage, setModalMessage] = useState('');
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});

    const handleToggleCurrentPassword = () => {
        setShowCurrentPassword((prev) => !prev);
    };

    const handleToggleNewPassword = () => {
        setShowNewPassword((prev) => !prev);
    };

    const handleToggleConfirmPassword = () => {
        setShowConfirmPassword((prev) => !prev);
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        
        // Clear field errors when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validatePassword = (password) => {
        const errors = [];
        if (password.length < 8) {
            errors.push('Password must be at least 8 characters long');
        }
        if (!/(?=.*[a-z])/.test(password)) {
            errors.push('Password must contain at least one lowercase letter');
        }
        if (!/(?=.*[A-Z])/.test(password)) {
            errors.push('Password must contain at least one uppercase letter');
        }
        if (!/(?=.*\d)/.test(password)) {
            errors.push('Password must contain at least one number');
        }
        if (!/(?=.*[@$!%*?&])/.test(password)) {
            errors.push('Password must contain at least one special character (@$!%*?&)');
        }
        return errors;
    };

    const handleChangePassword = async () => {
        // Clear previous errors
        setPasswordErrors([]);
        setFieldErrors({});

        // Validate required fields
        if (!formData.current_password || !formData.new_password || !formData.new_password_confirm) {
            setModalStatus('error');
            setModalMessage('Please fill in all fields.');
            setModalOpen(true);
            return;
        }

        // Validate new password strength
        const passwordValidationErrors = validatePassword(formData.new_password);
        if (passwordValidationErrors.length > 0) {
            setPasswordErrors(passwordValidationErrors);
            return;
        }

        // Validate password confirmation
        if (formData.new_password !== formData.new_password_confirm) {
            setFieldErrors({
                new_password_confirm: 'Passwords do not match'
            });
            return;
        }

        // Check if new password is different from current
        if (formData.current_password === formData.new_password) {
            setFieldErrors({
                new_password: 'New password must be different from current password'
            });
            return;
        }

        setLoading(true);
        
        try {
            const response = await AuthService.changePassword(formData.current_password, formData.new_password);
            
            setModalStatus('success');
            setModalMessage('Password changed successfully! You can now use your new password to log in.');
            setModalOpen(true);
            
            // Clear the form
            setFormData({ current_password: '', new_password: '', new_password_confirm: '' });
            
        } catch (error) {
            console.error('Change password error:', error);
            setModalStatus('error');
            setModalMessage(error.message || 'Password change failed. Please try again.');
            setModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        if (modalStatus === 'success') {
            navigate('/portal');
        }
    };

    const handleBackToPortal = () => {
        navigate('/portal');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={10}
                    sx={{
                        p: 4,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    {/* Header */}
                    <Box textAlign="center" mb={4}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#2c5aa0', fontWeight: 'bold' }}>
                            Change Password
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Enter your current password and choose a new one
                        </Typography>
                    </Box>

                    {/* Current Password Field */}
                    <TextField
                        type={showCurrentPassword ? "text" : "password"}
                        label="Current Password"
                        fullWidth
                        value={formData.current_password}
                        onChange={handleInputChange('current_password')}
                        error={!!fieldErrors.current_password}
                        helperText={fieldErrors.current_password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggleCurrentPassword}
                                        edge="end"
                                    >
                                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    {/* New Password Field */}
                    <TextField
                        type={showNewPassword ? "text" : "password"}
                        label="New Password"
                        fullWidth
                        value={formData.new_password}
                        onChange={handleInputChange('new_password')}
                        error={!!fieldErrors.new_password}
                        helperText={fieldErrors.new_password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggleNewPassword}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    {/* New Password Confirmation Field */}
                    <TextField
                        type={showConfirmPassword ? "text" : "password"}
                        label="Confirm New Password"
                        fullWidth
                        value={formData.new_password_confirm}
                        onChange={handleInputChange('new_password_confirm')}
                        error={!!fieldErrors.new_password_confirm}
                        helperText={fieldErrors.new_password_confirm}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggleConfirmPassword}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    {/* Password Strength Errors */}
                    {passwordErrors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            <Typography variant="body2" component="div">
                                Password requirements:
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                    {passwordErrors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </Typography>
                        </Alert>
                    )}

                    {/* Submit Button */}
                    <Button
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleChangePassword}
                        disabled={!formData.current_password || !formData.new_password || !formData.new_password_confirm || loading || passwordErrors.length > 0}
                        sx={{
                            py: 1.5,
                            backgroundColor: '#2D5EFF',
                            '&:hover': {
                                backgroundColor: '#1e4bd8'
                            },
                            mb: 2
                        }}
                    >
                        {loading ? 'Changing Password...' : 'Change Password'}
                    </Button>

                    {/* Back to Portal Button */}
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackToPortal}
                        disabled={loading}
                        sx={{
                            py: 1.5,
                            borderColor: '#2D5EFF',
                            color: '#2D5EFF',
                            '&:hover': {
                                borderColor: '#1e4bd8',
                                backgroundColor: 'rgba(45, 94, 255, 0.04)'
                            }
                        }}
                    >
                        Back to Portal
                    </Button>

                    {/* Success/Error Modal */}
                    <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                        <DialogContent sx={{ pt: 3 }}>
                            <Box textAlign="center">
                                {modalStatus === 'success' ? (
                                    <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                                ) : (
                                    <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                                )}
                                <Typography variant="h6" gutterBottom>
                                    {modalStatus === 'success' ? 'Password Changed!' : 'Error'}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {modalMessage}
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                            <Button
                                onClick={handleCloseModal}
                                variant="contained"
                                sx={{
                                    backgroundColor: modalStatus === 'success' ? '#2D5EFF' : '#f44336',
                                    '&:hover': {
                                        backgroundColor: modalStatus === 'success' ? '#1e4bd8' : '#d32f2f'
                                    }
                                }}
                            >
                                {modalStatus === 'success' ? 'Continue to Portal' : 'Try Again'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Container>
        </Box>
    );
};

export default ChangePassword;
